import styled from 'styled-components';
import { Button } from '../../../Button';
import { CardStatus } from '../../enums/CardStatus';

export const TrainingCard = styled.div<{ $wrap?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  gap: 8px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  background-color: ${({ theme }) => theme.colors.prototype.card};

  .plus-circled-icon {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.prototype.primary};
  }
`;

export const IconContainer = styled.div`
  width: 24px;
  height: 24px;
`;

export const Title = styled.span`
  color: ${({ theme }) => theme.colors.prototype.foreground};
  display: block;
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TrainingPrimaryButton = styled(Button).attrs({ size: 'small' })`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.prototype.primary};
  color: ${({ theme }) => theme.colors.prototype.accentForeground};
  font-size: 12px;
  transition: all 250ms ease-in-out;

  @media (max-width: 768px) {
    padding: 16px 24px;
  }
`;

export const TrainingSecondaryButton = styled(Button).attrs({ size: 'small' })<{
  $isLoading?: boolean;
}>`
  padding: 6px 12px;
  background-color: ${({ theme, $isLoading }) =>
    $isLoading === undefined ? theme.colors.prototype.primary : theme.colors.prototype.secondary};
  color: ${({ theme, $isLoading }) =>
    $isLoading === undefined ? theme.colors.prototype.accentForeground : theme.colors.prototype.primary};
  font-size: 12px;
  transition: all 250ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.prototype.primary};
    color: ${({ theme }) => theme.colors.prototype.accentForeground};
  }
`;

export const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  gap: 8px;
`;

export const ProgressText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

export const ProgressBar = styled.div`
  flex-grow: 1;
  height: 8px;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.prototype.card};
  overflow: hidden;

  @media (max-width: 768px) {
    height: 16px;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: unset;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 16px;
  margin-right: auto;
  min-width: 0;
`;

export const MainTrainingCard = styled(TrainingCard).withConfig({
  shouldForwardProp: (prop) => prop !== 'cardStatus',
})<{ cardStatus: CardStatus }>`
  flex-direction: column;
  align-items: unset;
  background-color: ${({ cardStatus, theme }) =>
    cardStatus === CardStatus.urgent
      ? theme.colors.prototype.destructive.replace(')', ' / 10%)')
      : cardStatus === CardStatus.warning
        ? theme.colors.prototype.warning.replace(')', ' / 10%)')
        : theme.colors.prototype.card};

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

export const DateLabelContainer = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== 'cardStatus',
})<{ cardStatus: CardStatus }>`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  color: ${({ cardStatus, theme }) =>
    cardStatus === CardStatus.urgent
      ? theme.colors.prototype.destructive
      : cardStatus === CardStatus.warning
        ? theme.colors.prototype.warningText
        : theme.colors.prototype.mutedForeground};

  @media (max-width: 768px) {
    gap: 16px;
    align-items: center;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const DateLabel = styled.div`
  font-size: 12px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const ProgressFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress' && prop !== 'backgroundColor',
})<{ progress: number; backgroundColor?: string }>`
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  background: ${({ backgroundColor, theme }) => backgroundColor || theme.colors.prototype.primary};
  transition: width 0.3s ease-in-out;
`;

/* ------------------------------------------------------------------ */
/* Prototype-style (compact) training cards — matches the CyberQ       */
/* prototype's TrainingCampaigns look. Used by the merged active list.  */
/* ------------------------------------------------------------------ */

export const ProtoActiveCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border.replace(')', ' / 50%)')};
  background-color: ${({ theme }) => theme.colors.prototype.card};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.prototype.border};
  }
`;

export const ProtoTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ProtoCardTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const ProtoDateLabel = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$urgent',
})<{ $urgent?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme, $urgent }) =>
    $urgent ? theme.colors.prototype.destructive : theme.colors.prototype.mutedForeground};
`;

export const ProtoBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

export const ProtoMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

export const ProtoPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  background-color: ${({ theme }) => theme.colors.prototype.primary};
  color: ${({ theme }) => theme.colors.prototype.primaryForeground};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.prototype.primary.replace(')', ' / 90%)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 11px;
    height: 11px;
  }
`;

export const ProtoAddButton = styled.button`
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.prototype.primary};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ProtoCompletedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
`;

export const ProtoCompletedInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

export const ProtoRevisitButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.accentText};

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const CompletedCheckIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.prototype.success};
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;
